import { json } from "@remix-run/node";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { Link, useOutletContext } from "@remix-run/react";
import type { Locale } from "~/utils/locale";
import { detectLocale, localePath } from "~/utils/locale";
import { t } from "~/utils/translations";
import { Breadcrumbs } from "~/components/storefront/Breadcrumbs";
import { buildPageMeta, originFromRequest } from "~/utils/storefrontMeta";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  return json({ locale: detectLocale(request.url), origin: originFromRequest(request) });
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const locale = (data?.locale || "vi") as Locale;
  const origin = data?.origin || "https://diolo.com";
  return buildPageMeta({
    title:
      locale === "vi"
        ? "Đổi trả — Diolo"
        : "Returns & Exchanges — Diolo",
    description:
      locale === "vi"
        ? "Chính sách Diolo — đổi hàng miễn phí trong 7 ngày kể từ ngày nhận. Không trả hàng, không hoàn tiền."
        : "Diolo policy — free exchanges within 7 days of delivery. No returns, no refunds.",
    path: localePath("/returns", locale),
    origin,
    locale,
  });
};

export default function ReturnsPage() {
  const { locale } = useOutletContext<{ locale: Locale }>();

  const vnConditions = [
    locale === "vi"
      ? "Đổi hàng trong vòng 7 ngày kể từ ngày nhận hàng"
      : "Exchanges within 7 days of delivery",
    locale === "vi"
      ? "Sản phẩm phải còn nguyên tem mác và bao bì gốc"
      : "Items must have original tags and packaging",
    locale === "vi"
      ? "Sản phẩm chưa qua sử dụng, giặt hoặc chỉnh sửa"
      : "Items must be unworn, unwashed, and unaltered",
    locale === "vi"
      ? "Diolo chịu phí vận chuyển đổi hàng — đổi miễn phí"
      : "Diolo covers exchange shipping — exchanges are free",
    locale === "vi"
      ? "Chỉ đổi size/màu — không hoàn tiền"
      : "Exchange only for size/color — no refunds",
  ];

  const intlConditions = [
    locale === "vi"
      ? "Đổi hàng trong vòng 7 ngày kể từ ngày nhận hàng"
      : "Exchanges within 7 days of delivery",
    locale === "vi"
      ? "Sản phẩm phải còn nguyên tem mác và bao bì gốc"
      : "Items must have original tags and packaging",
    locale === "vi"
      ? "Sản phẩm chưa qua sử dụng, giặt hoặc chỉnh sửa"
      : "Items must be unworn, unwashed, and unaltered",
    locale === "vi"
      ? "Chỉ đổi size/màu — không trả hàng, không hoàn tiền"
      : "Exchange only for size/colour — no returns, no refunds",
  ];

  return (
    <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-12">
      <Breadcrumbs locale={locale} items={[{ label: t("returns", locale) }]} />

      {/* Header */}
      <div className="text-center mb-12">
        <p className="text-xs uppercase tracking-widest text-luxury-stone mb-4">
          {locale === "vi" ? "Chính sách" : "Policy"}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-luxury-charcoal tracking-normal">
          {locale === "vi" ? "Đổi trả & Hoàn tiền" : "Returns & Exchanges"}
        </h1>
        <p className="mt-4 text-sm text-luxury-stone max-w-xl mx-auto">
          {locale === "vi"
            ? "Chính sách của chúng tôi tuỳ thuộc vào thị trường của bạn. Vui lòng chọn khu vực."
            : "Our policy depends on your market. Please choose your region below."}
        </p>
      </div>

      {/* Two-card layout: Vietnam vs International */}
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        {/* Vietnam card */}
        <section className="bg-white border border-luxury-sand p-8">
          <p className="text-[10px] uppercase tracking-[0.2em] text-luxury-stone mb-3">
            {t("imInVietnam", locale)}
          </p>
          <h2 className="font-display text-2xl font-light text-luxury-charcoal mb-2">
            {t("vietnamExchangePolicy", locale)}
          </h2>
          <p className="text-sm text-luxury-charcoal mb-6">
            {locale === "vi"
              ? "Đổi hàng trong 7 ngày — không hoàn tiền"
              : "7-day exchanges — no refunds"}
          </p>
          <ul className="space-y-3 mb-8">
            {vnConditions.map((item, idx) => (
              <li
                key={idx}
                className="flex items-start gap-3 text-xs text-luxury-charcoal"
              >
                <span
                  className="mt-[6px] block h-[3px] w-[3px] bg-luxury-charcoal"
                  aria-hidden
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <Link
            to={localePath("/order-tracking?startReturn=1", locale)}
            className="sf-btn-secondary text-xs"
          >
            {t("requestExchange", locale)}
          </Link>
        </section>

        {/* International card */}
        <section className="bg-[#faf8f3] border border-luxury-sand p-8">
          <p className="text-[10px] uppercase tracking-[0.2em] text-luxury-stone mb-3">
            {t("imInternational", locale)}
          </p>
          <h2 className="font-display text-2xl font-light text-luxury-charcoal mb-2">
            {locale === "vi"
              ? "Chính sách đổi hàng quốc tế"
              : "International Exchange Policy"}
          </h2>
          <p className="text-sm text-luxury-charcoal mb-6">
            {locale === "vi"
              ? "Đổi hàng trong 7 ngày — không hoàn tiền"
              : "7-day exchanges — no refunds"}
          </p>
          <ul className="space-y-3 mb-8">
            {intlConditions.map((item, idx) => (
              <li
                key={idx}
                className="flex items-start gap-3 text-xs text-luxury-charcoal"
              >
                <span
                  className="mt-[6px] block h-[3px] w-[3px] bg-luxury-charcoal"
                  aria-hidden
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <Link
            to={localePath("/order-tracking?startReturn=1", locale)}
            className="sf-btn-secondary text-xs"
          >
            {t("requestReturn", locale)}
          </Link>
        </section>
      </div>

      {/* How it works */}
      <div className="max-w-2xl mx-auto">
        <h2 className="font-display text-lg font-light text-luxury-charcoal tracking-normal mb-6 text-center">
          {locale === "vi" ? "Quy trình" : "How it Works"}
        </h2>
        <div className="space-y-6">
          {[
            {
              num: "01",
              title: locale === "vi" ? "Tra cứu đơn hàng" : "Look Up Your Order",
              desc:
                locale === "vi"
                  ? "Nhập mã đơn hàng và email để mở chi tiết — không cần đăng nhập."
                  : "Enter your order number and email to open the order details — no sign-in needed.",
            },
            {
              num: "02",
              title: locale === "vi" ? "Chờ phê duyệt" : "Await Approval",
              desc:
                locale === "vi"
                  ? "Đội ngũ của chúng tôi sẽ phản hồi trong vòng 1-2 ngày làm việc."
                  : "Our team will respond within 1-2 business days.",
            },
            {
              num: "03",
              title: locale === "vi" ? "Gửi hàng trả" : "Ship It Back",
              desc:
                locale === "vi"
                  ? "Làm theo hướng dẫn gửi hàng trong email phê duyệt."
                  : "Follow the shipping instructions in the approval email.",
            },
            {
              num: "04",
              title:
                locale === "vi"
                  ? "Nhận sản phẩm thay thế / hoàn tiền"
                  : "Receive Replacement / Refund",
              desc:
                locale === "vi"
                  ? "Chúng tôi sẽ gửi sản phẩm đổi hoặc xử lý hoàn tiền trong 5-7 ngày sau khi nhận."
                  : "We ship your replacement or process your refund within 5-7 days of receipt.",
            },
          ].map((step) => (
            <div key={step.num} className="flex gap-5">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-luxury-cream flex items-center justify-center">
                <span className="text-xs font-display tracking-normal text-luxury-charcoal">
                  {step.num}
                </span>
              </div>
              <div className="pt-1">
                <h3 className="text-sm font-display uppercase tracking-normal text-luxury-charcoal mb-1">
                  {step.title}
                </h3>
                <p className="text-luxury-stone text-sm leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export { RouteErrorBoundary as ErrorBoundary } from "~/components/RouteErrorBoundary";
